/** Separadores con los que la gente pega varios números en una celda: «/», «,», «;», «|», salto de línea, guion largo o « - », « y », « o ». */
const PHONE_SEPARATOR = /\s*(?:[/,;|\n]|–|—|\s-\s|\s(?:y|o)\s)\s*/i;
/** Menos dígitos que esto no es un teléfono (ni un trozo de uno). */
export const PHONE_MIN_DIGITS = 6;
/** Tope de E.164 (15 dígitos): un celular de Brasil o Argentina por WhatsApp tiene 13; más que esto son dos números pegados o basura. */
export const PHONE_MAX_DIGITS = 15;
/**
 * Fijo peruano sin el prefijo internacional: Lima (1 + 7 dígitos) o provincia (código de dos dígitos + 6). Con 10
 * dígitos que empiezan en 51 solo se quita el prefijo si lo que queda tiene esta forma; así «5121234567» o
 * «5101234567» (código de área 512 / 510 de EE. UU. escrito sin el +1) se dejan como están.
 */
const PERU_LANDLINE = /^(?:1\d{7}|(?:4[1-4]|5[1-46]|6[1-7]|7[2-46]|8[2-4])\d{6})$/;

/**
 * Un solo número: solo dígitos, sin ceros iniciales ni prefijo 51 (celular 51 + 9 dígitos, fijo 51 + 1 + 7 de Lima o
 * 51 + código de provincia + 6 dígitos, siempre 10). Devuelve null con menos de `PHONE_MIN_DIGITS` o más de
 * `PHONE_MAX_DIGITS` dígitos.
 */
function normalizeSinglePhone(raw: string): string | null {
  let digits = raw.replace(/\D+/g, '');
  if (!digits) return null;
  digits = digits.replace(/^0+/, '');
  if (digits.length > PHONE_MAX_DIGITS) return null;
  if (digits.startsWith('51') && digits.length === 11) digits = digits.slice(2);
  else if (digits.startsWith('51') && digits.length === 10 && PERU_LANDLINE.test(digits.slice(2)))
    digits = digits.slice(2);
  else if (digits.startsWith('51') && digits.length === 9 && digits[2] !== '9') {
    // fijo de Lima pegado al prefijo sin código de ciudad: 51 XXXXXXX
    digits = digits.slice(2);
  }
  if (digits.length < PHONE_MIN_DIGITS) return null;
  return digits;
}

/**
 * Todos los números que trae un texto (una celda de Excel con «987654321 / 912345678» son dos), normalizados y sin
 * repetidos, en el orden en que aparecen. El primero es el celular principal; el resto se guarda como alternos.
 * Solo se parte cuando cada trozo con dígitos tiene al menos `PHONE_MIN_DIGITS`: «987 - 654 - 321» u «01 - 4456677»
 * son un solo número escrito con guiones, no varios.
 */
export function normalizePhones(raw: string | null | undefined): string[] {
  if (!raw) return [];
  const parts = raw.split(PHONE_SEPARATOR).filter((p) => /\d/.test(p));
  const pieces = parts.every((p) => p.replace(/\D+/g, '').length >= PHONE_MIN_DIGITS)
    ? parts
    : [raw];
  const out: string[] = [];
  for (const part of pieces) {
    const n = normalizeSinglePhone(part);
    if (n && !out.includes(n)) out.push(n);
  }
  return out;
}

/** Teléfono: solo dígitos, sin prefijo internacional 51 ni ceros iniciales. Con varios números, el primero. */
export function normalizePhone(raw: string | null | undefined): string | null {
  return normalizePhones(raw)[0] ?? null;
}

/**
 * Por qué un celular escrito no se puede normalizar, para decírselo a quien lo escribió (registro a mano y ficha):
 * demasiados dígitos (más de `PHONE_MAX_DIGITS`) o muy pocos (menos de `PHONE_MIN_DIGITS`). Null si sí se puede.
 */
export function invalidPhoneReason(raw: string | null | undefined): string | null {
  if (!raw || normalizePhone(raw)) return null;
  const digits = raw.replace(/\D+/g, '').replace(/^0+/, '').length;
  return digits > PHONE_MAX_DIGITS
    ? `tiene ${digits} dígitos y el máximo es ${PHONE_MAX_DIGITS}`
    : `escribe al menos ${PHONE_MIN_DIGITS} dígitos`;
}

/** Correo: minúsculas y sin espacios. Devuelve null si no tiene forma de correo. */
export function normalizeEmail(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const email = raw.trim().toLowerCase();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return null;
  return email;
}

const ENIE_LOWER = '\u0001';
const ENIE_UPPER = '\u0002';

/** Nombres: mayúsculas, sin tildes (la Ñ se conserva), sin dobles espacios. Para cruces por nombre (último recurso). */
export function normalizeName(raw: string | null | undefined): string {
  if (!raw) return '';
  // Se protege la ñ antes de descomponer (NFD la separaría en n + virgulilla).
  return raw
    .replaceAll('ñ', ENIE_LOWER)
    .replaceAll('Ñ', ENIE_UPPER)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replaceAll(ENIE_LOWER, 'ñ')
    .replaceAll(ENIE_UPPER, 'Ñ')
    .toUpperCase()
    .replace(/[^A-Z0-9Ñ ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Qué buscar con un texto libre (tabla, tablero y buscador global). */
export interface SearchTerms {
  /** Dígitos para comparar con el celular normalizado (sin +51 ni ceros); null con menos de 4 dígitos. */
  phone: string | null;
  /** Dígitos tal como se escribieron, para el documento; null con menos de 4 dígitos. */
  document: string | null;
  /** Correo en minúsculas cuando el texto lleva «@». */
  email: string | null;
  /** Palabras del nombre normalizado: todas deben aparecer («Juan Pérez» encuentra a «Juan Carlos Pérez»). */
  words: string[];
}

/**
 * Interpreta lo que escribió el usuario: con 4 dígitos o más se busca por celular (con o sin +51, como se copia
 * de WhatsApp) y documento; con «@», por correo; si no, por las palabras del nombre.
 */
export function parseSearchQuery(q: string): SearchTerms {
  const digits = q.replace(/\D+/g, '');
  const email = q.includes('@') ? q.trim().toLowerCase() : null;
  if (digits.length >= 4) {
    return { phone: normalizePhone(digits) ?? digits, document: digits, email, words: [] };
  }
  const words = email ? [] : normalizeName(q).split(' ').filter(Boolean);
  return { phone: null, document: null, email, words };
}

/** Un DNI tiene 8 dígitos: Excel suele comerse los ceros iniciales («01234567» llega como «1234567»). */
const DNI_LENGTH = 8;

/**
 * Documento: mayúsculas, solo alfanumérico, siempre como texto (nunca como número, para no perder ceros iniciales).
 * Un DNI escrito con la palabra delante («DNI 01234567») pierde el prefijo, y uno de solo dígitos al que Excel le quitó
 * los ceros se rellena hasta 8. Devuelve null con menos de 6 caracteres.
 */
export function normalizeDocument(
  raw: string | null | undefined,
  documentType?: string | null,
): string | null {
  if (!raw) return null;
  let doc = raw.toUpperCase().replace(/[^A-Z0-9]+/g, '');
  const dniPrefixed = /^DNI\d+$/.test(doc);
  if (dniPrefixed) doc = doc.slice(3);
  if ((documentType === 'DNI' || dniPrefixed || !documentType) && /^\d{6,7}$/.test(doc))
    doc = doc.padStart(DNI_LENGTH, '0');
  return doc.length >= 6 ? doc : null;
}

export function isTestEmail(
  email: string | null | undefined,
  patterns: readonly string[],
): boolean {
  if (!email) return false;
  const upper = email.toUpperCase();
  return patterns.some((p) => upper.includes(p.toUpperCase()));
}

/** Enlace wa.me con el número peruano en formato internacional. */
export function whatsappLink(phoneNormalized: string, text?: string): string {
  const intl = phoneNormalized.length === 9 ? `51${phoneNormalized}` : phoneNormalized;
  const base = `https://wa.me/${intl}`;
  return text ? `${base}?text=${encodeURIComponent(text)}` : base;
}
